import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * Bank Reconciliation Rules CRUD API
 * GET    /api/bank/rules?empresa_id=X&activa=true  - List rules
 * POST   /api/bank/rules                            - Create rule
 * POST   /api/bank/rules?action=generate             - Auto-generate rules from history
 * PUT    /api/bank/rules                             - Update rule
 * DELETE /api/bank/rules?id=X                        - Deactivate rule (soft delete)
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  const supabaseAdmin = getSupabaseAdminClient();

  try {
    // ─── GET: List rules ───
    if (req.method === 'GET') {
      const { empresa_id, activa } = req.query;

      if (!empresa_id) {
        return res.status(400).json({ success: false, error: 'empresa_id es obligatorio.' });
      }

      let query = supabaseAdmin
        .from('bank_rules')
        .select('*')
        .eq('empresa_id', empresa_id as string)
        .order('prioridad', { ascending: true })
        .order('nombre', { ascending: true });

      if (activa !== undefined) {
        query = query.eq('activa', activa === 'true');
      }

      const { data, error } = await query;

      if (error) {
        console.error('[API bank/rules] Error listing rules:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      return res.status(200).json({ success: true, data });
    }

    // ─── POST: Create rule or auto-generate ───
    if (req.method === 'POST') {
      const { action } = req.query;

      // Auto-generate rules from historical data
      if (action === 'generate') {
        return await handleGenerateRules(req, res, user, supabaseAdmin);
      }

      // Create a new rule
      const {
        empresa_id,
        nombre,
        condiciones,
        contrapartida,
        descripcion_asiento,
        tipo_movimiento,
        iva_aplicable,
        prioridad,
      } = req.body;

      if (!empresa_id || !nombre || !condiciones || !contrapartida) {
        return res.status(400).json({
          success: false,
          error: 'empresa_id, nombre, condiciones y contrapartida son obligatorios.',
        });
      }

      if (!Array.isArray(condiciones) || condiciones.length === 0) {
        return res.status(400).json({
          success: false,
          error: 'condiciones debe ser un array no vacío.',
        });
      }

      // Validate each condition
      const validOperators = ['contiene', 'es_igual', 'comienza_con', 'termina_con', 'mayor_que', 'menor_que', 'rango'];
      for (const cond of condiciones) {
        if (!cond.campo || !cond.operador || cond.valor === undefined) {
          return res.status(400).json({
            success: false,
            error: 'Cada condición debe tener campo, operador y valor.',
          });
        }
        if (!validOperators.includes(cond.operador)) {
          return res.status(400).json({
            success: false,
            error: `Operador inválido: ${cond.operador}. Permitidos: ${validOperators.join(', ')}`,
          });
        }
      }

      const { data, error } = await supabaseAdmin
        .from('bank_rules')
        .insert({
          empresa_id,
          nombre,
          condiciones,
          contrapartida,
          descripcion_asiento: descripcion_asiento || null,
          tipo_movimiento: tipo_movimiento || null,
          iva_aplicable: iva_aplicable ?? null,
          prioridad: prioridad ?? 100,
          activa: true,
          origen: 'manual',
          veces_aplicada: 0,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        console.error('[API bank/rules] Error creating rule:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      return res.status(201).json({ success: true, data });
    }

    // ─── PUT: Update rule ───
    if (req.method === 'PUT') {
      const { id, ...updates } = req.body;

      if (!id) {
        return res.status(400).json({ success: false, error: 'id es obligatorio.' });
      }

      if (!updates || Object.keys(updates).length === 0) {
        return res.status(400).json({
          success: false,
          error: 'No se proporcionaron campos para actualizar.',
        });
      }

      // Validate conditions if provided
      if (updates.condiciones) {
        if (!Array.isArray(updates.condiciones) || updates.condiciones.length === 0) {
          return res.status(400).json({
            success: false,
            error: 'condiciones debe ser un array no vacío.',
          });
        }
      }

      // Remove fields that should not be updated directly
      delete updates.created_at;
      delete updates.created_by;
      delete updates.veces_aplicada;

      const updateData: Record<string, unknown> = { ...updates, updated_at: new Date().toISOString() };

      const { data, error } = await supabaseAdmin
        .from('bank_rules')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[API bank/rules] Error updating rule:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      if (!data) {
        return res.status(404).json({ success: false, error: 'Regla no encontrada.' });
      }

      return res.status(200).json({ success: true, data });
    }

    // ─── DELETE: Deactivate rule (soft delete) ───
    if (req.method === 'DELETE') {
      const { id } = req.query;

      if (!id) {
        return res.status(400).json({ success: false, error: 'id es obligatorio.' });
      }

      const { data, error } = await supabaseAdmin
        .from('bank_rules')
        .update({ activa: false, updated_at: new Date().toISOString() })
        .eq('id', id as string)
        .select()
        .single();

      if (error) {
        console.error('[API bank/rules] Error deactivating rule:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      if (!data) {
        return res.status(404).json({ success: false, error: 'Regla no encontrada.' });
      }

      return res.status(200).json({ success: true, data });
    }

    return res.status(405).json({ success: false, error: 'Method not allowed' });
  } catch (error: any) {
    console.error('[API bank/rules] Unexpected error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error inesperado en reglas de conciliación.',
    });
  }
}

/**
 * Auto-generate reconciliation rules from historical reconciled movements.
 * Analyzes patterns in already-reconciled movements to suggest new rules.
 */
async function handleGenerateRules(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string },
  supabaseAdmin: ReturnType<typeof getSupabaseAdminClient>
) {
  const { empresa_id, bank_account_id } = req.body;

  if (!empresa_id) {
    return res.status(400).json({ success: false, error: 'empresa_id es obligatorio.' });
  }

  // Fetch reconciled movements to analyze patterns
  let query = supabaseAdmin
    .from('bank_movements')
    .select('concepto, tipo_movimiento, importe, documento_asociado_id, regla_aplicada_id')
    .eq('empresa_id', empresa_id)
    .eq('estado_conciliacion', 'conciliado')
    .not('concepto', 'is', null)
    .order('fecha_operacion', { ascending: false })
    .limit(500);

  if (bank_account_id) {
    query = query.eq('bank_account_id', bank_account_id);
  }

  const { data: movements, error: movError } = await query;

  if (movError) {
    return res.status(500).json({ success: false, error: movError.message });
  }

  if (!movements || movements.length < 5) {
    return res.status(200).json({
      success: true,
      data: [],
      message: 'No hay suficientes movimientos conciliados para generar reglas (mínimo 5).',
    });
  }

  // Group movements by concept patterns
  const conceptGroups = new Map<string, { count: number; tipo: string | null; conceptos: string[] }>();

  for (const mov of movements) {
    if (!mov.concepto) continue;

    // Extract key words (remove numbers, dates, references)
    const normalized = mov.concepto
      .toLowerCase()
      .replace(/\d{2}[/-]\d{2}[/-]\d{2,4}/g, '') // Remove dates
      .replace(/\b\d+\b/g, '') // Remove standalone numbers
      .replace(/\s+/g, ' ')
      .trim();

    // Use first 3 significant words as pattern key
    const words = normalized.split(' ').filter((w: string) => w.length > 2).slice(0, 3);
    if (words.length === 0) continue;
    const patternKey = words.join(' ');

    const existing = conceptGroups.get(patternKey);
    if (existing) {
      existing.count++;
      existing.conceptos.push(mov.concepto);
    } else {
      conceptGroups.set(patternKey, {
        count: 1,
        tipo: mov.tipo_movimiento,
        conceptos: [mov.concepto],
      });
    }
  }

  // Generate rules for patterns that appear 3+ times
  const generatedRules: Array<Record<string, unknown>> = [];
  let prioridad = 200; // Start after manual rules

  for (const [pattern, group] of Array.from(conceptGroups.entries())) {
    if (group.count < 3) continue;

    const ruleName = `Auto: ${pattern.substring(0, 50)}`;
    const condiciones = [
      {
        campo: 'concepto',
        operador: 'contiene',
        valor: pattern.split(' ')[0], // Use first keyword
      },
    ];

    // Check if a similar rule already exists
    const { data: existingRule } = await supabaseAdmin
      .from('bank_rules')
      .select('id')
      .eq('empresa_id', empresa_id)
      .ilike('nombre', `%${pattern.substring(0, 20)}%`)
      .maybeSingle();

    if (existingRule) continue;

    const { data: rule, error: ruleError } = await supabaseAdmin
      .from('bank_rules')
      .insert({
        empresa_id,
        nombre: ruleName,
        condiciones,
        contrapartida: '', // Needs manual review
        descripcion_asiento: `Generada automáticamente desde ${group.count} movimientos`,
        tipo_movimiento: group.tipo || null,
        prioridad,
        activa: false, // Inactive until manually reviewed
        origen: 'historico',
        veces_aplicada: 0,
        created_by: user.id,
      })
      .select()
      .single();

    if (!ruleError && rule) {
      generatedRules.push(rule);
      prioridad++;
    }
  }

  return res.status(200).json({
    success: true,
    data: generatedRules,
    message: `${generatedRules.length} regla(s) generada(s). Revisa y activa las que correspondan.`,
  });
}

export default withAuth(handler);
